import { FC, Dispatch, SetStateAction, useEffect, useState } from 'react'
import StepBullet from 'src/components/basic/StepBullet'
import StandardInput from 'src/components/basic/StandardInput'
import StandardFormatInput from 'src/components/basic/StandardFormatInput'
import AddressFormCustom from 'src/components/application/formComponents/AddressFormCustom'
import StandardCheckbox from 'src/components/basic/StandardCheckbox'
import Button from 'src/components/basic/Button'
import {
  FormContainer,
  FormItemContainer,
} from 'src/components/application/defaultComponents'
import { JudgeBasicSectionData } from 'src/dtos/applicationData'
import QuestionItemGeneralLayout from '../../formComponents/QuestionItemGeneralLayout'
import { validateDateField } from 'src/utils/formDataManipulation'
import BarCardNumberInput from 'src/components/basic/BarCardNumberInput'
import { useErrors } from 'src/context/errors'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'

interface Props {
  judgeBasicData: JudgeBasicSectionData;
  setJudgeBasicData: Dispatch<SetStateAction<JudgeBasicSectionData>>;
  validateSteps: { [key: string]: boolean; };
  validatedFields: string[]
  isRenewal?: boolean;
}

const BasicForm: FC<Props> = ({
  judgeBasicData,
  setJudgeBasicData,
  validateSteps,
  validatedFields,
  isRenewal
}) => {
  const mailingAddressErrorCodes = ['EC4', 'EC5', 'EC6', 'EC7', 'EC8']
  const [willScroll, setWillScroll] = useState(0)
  const [addFax, setAddFax] = useState(!!judgeBasicData?.contactInfo?.faxNumber)

  useEffect(() => {
    if(validatedFields.length > 0 && validatedFields.length > willScroll) {
      document.getElementById(validatedFields[0])?.scrollIntoView({ behavior: 'smooth' })
    }
    setWillScroll(validatedFields.length)
  }, [validatedFields, willScroll])

  return (
    <FormContainer maxWidth='624px'>
      <QuestionItemGeneralLayout
        step={1}
        title='Full Name of Judge'
        leftBar
        isValid={validateSteps['step1']}
      >
        <StandardInput
          label="First Name"
          value={judgeBasicData?.firstName ?? ''}
          onChange={({ target: { value } }) => setJudgeBasicData(prev => {
            let contactFirstName = prev?.contactInfo?.firstName
            if(prev?.contactInfo?.isSameAsJudge === true) {
              contactFirstName = value
            }
            return { ...prev, firstName: value, contactInfo: { ...prev?.contactInfo, firstName: contactFirstName }}
          })}
          isIncorrect={validatedFields.includes('step1') && !judgeBasicData?.firstName}
          errorMessage={(validatedFields.includes('step1') && !judgeBasicData?.firstName) ? "This is a required field" : ""}
          errorCode='EC135'
        />
        <StandardInput
          label="Last Name"
          value={judgeBasicData?.lastName ?? ''}
          onChange={({ target: { value } }) => setJudgeBasicData(prev => {
            let contactLastName = prev?.contactInfo?.firstName
            if(prev?.contactInfo?.isSameAsJudge === true) {
              contactLastName = value
            }
            return { ...prev, lastName: value, contactInfo: { ...prev?.contactInfo, lastName: contactLastName }}
          })}
          isIncorrect={validatedFields.includes('step1') && !judgeBasicData?.lastName}
          errorMessage={(validatedFields.includes('step1') && !judgeBasicData?.lastName) ? "This is a required field" : ""}
          errorCode='EC136'
        />
      </QuestionItemGeneralLayout>
      <QuestionItemGeneralLayout
        step={2}
        title='Contact Info'
        subtitle='Who should we contact with questions about your application?'
        leftBar
        isValid={validateSteps['step2']}
      >
        <StandardInput
          label="First Name"
          disabled={judgeBasicData?.contactInfo?.isSameAsJudge === true}
          value={judgeBasicData?.contactInfo?.firstName ?? ''}
          onChange={e => setJudgeBasicData(prev => ({
            ...prev,
            contactInfo: {
              ...prev?.contactInfo,
              firstName: e.target.value
            }
          }))}
          isIncorrect={validatedFields.includes('step2') && !judgeBasicData?.contactInfo?.firstName}
          errorMessage={(validatedFields.includes('step2') && !judgeBasicData?.contactInfo?.firstName) ? "This is a required field" : ""}
          errorCode='EC137'
        />
        <StandardInput
          label="Last Name"
          disabled={judgeBasicData?.contactInfo?.isSameAsJudge === true}
          value={judgeBasicData?.contactInfo?.lastName ?? ''}
          onChange={e => setJudgeBasicData(prev => ({
            ...prev,
            contactInfo: {
              ...prev?.contactInfo,
              lastName: e.target.value
            }
          }))}
          isIncorrect={validatedFields.includes('step2') && !judgeBasicData?.contactInfo?.lastName}
          errorMessage={(validatedFields.includes('step2') && !judgeBasicData?.contactInfo?.lastName) ? "This is a required field" : ""}
          errorCode='EC138'
        />
        <StandardCheckbox
          label="Same as Judge listed above"
          variation='small'
          checked={judgeBasicData?.contactInfo?.isSameAsJudge === true}
          onChange={() => {}}
          onClick={() => setJudgeBasicData(prev => {
            const isSameAsJudge = !prev?.contactInfo?.isSameAsJudge
            let firstName = undefined
            let lastName = undefined
            if(prev?.contactInfo?.isSameAsJudge !== true) {
              firstName = prev.firstName
              lastName = prev.lastName
            }
            return {
              ...prev,
              contactInfo: {
                ...prev?.contactInfo,
                isSameAsJudge,
                firstName,
                lastName
              }
            }
          })}
        />
        <StandardFormatInput
          label='Phone Number'
          maskStyle='phone'
          value={judgeBasicData?.contactInfo?.phoneNumber ?? ''}
          onValueChange={({ formattedValue }) => setJudgeBasicData(prev => ({
            ...prev,
            contactInfo: {
              ...prev?.contactInfo,
              phoneNumber: formattedValue
            }
          }))}
          isIncorrect={validatedFields.includes('step2') && !judgeBasicData?.contactInfo?.phoneNumber}
          errorMessage={(validatedFields.includes('step2') && !judgeBasicData?.contactInfo?.phoneNumber) ? "This is a required field" : ""}
          errorCode='EC139'
        />
        <StandardInput
          label="Email Address"
          type="email"
          value={judgeBasicData?.contactInfo?.emailAddress ?? ''}
          onChange={e => setJudgeBasicData(prev => ({
            ...prev,
            contactInfo: {
              ...prev?.contactInfo,
              emailAddress: e.target.value
            }
          }))}
          isIncorrect={validatedFields.includes('step2') && !judgeBasicData?.contactInfo?.emailAddress}
          errorMessage={(validatedFields.includes('step2') && !judgeBasicData?.contactInfo?.emailAddress) ? "This is a required field" : ""}
          errorCode='EC140'
        />
        {addFax ? (
          <StandardFormatInput
            label='Fax Number'
            maskStyle='phone'
            value={judgeBasicData?.contactInfo?.faxNumber ?? ''}
            onValueChange={({ formattedValue }) => setJudgeBasicData(prev => ({
              ...prev,
              contactInfo: {
                ...prev?.contactInfo,
                faxNumber: formattedValue
              }
            }))}
          />
        ) : (
          <Button
            variant='secondary'
            onClick={() => setAddFax(true)}
          >
            Add fax number
          </Button>
        )}
        <ValidationErrorMessage errorCode='EC175' />
      </QuestionItemGeneralLayout>
      <FormItemContainer maxWidth='660px'>
        <StepBullet id="step3" isComplete={validateSteps.step3}>3</StepBullet>
        <AddressFormCustom
          title='Principal office address'
          setAddressData={addressData => setJudgeBasicData(prev => ({ ...prev, principalOfficeAddress: { ...prev.principalOfficeAddress, ...addressData }}))}
          addressData={judgeBasicData?.principalOfficeAddress}
          isIncorrect={validatedFields.includes('step3')}
          errorCodes={mailingAddressErrorCodes}
        />

      </FormItemContainer>
      <div style={{ paddingLeft: '32px' }}>
        <ValidationErrorMessage errorCode='EC176' />
      </div>
      <QuestionItemGeneralLayout
        step={4}
        title='County of the court over which the applicant presides.'
        isValid={validateSteps['step4']}
      >
        <StandardInput
          label=""
          value={judgeBasicData?.presidingCounty ?? ''}
          onChange={e => setJudgeBasicData(prev => ({ ...prev, presidingCounty: e.target.value }))}
          isIncorrect={validatedFields.includes('step4') && !judgeBasicData?.presidingCounty}
          errorMessage={(validatedFields.includes('step4') && !judgeBasicData?.presidingCounty) ? "This is a required field" : ""}
          errorCode='EC141'
        />
      </QuestionItemGeneralLayout>
      {isRenewal === true ? null : (
        <>
          <QuestionItemGeneralLayout
            step={5}
            title='Court over which applicant presides'
            isValid={validateSteps['step5']}
          >
            <StandardInput
              label=""
              value={judgeBasicData?.residingCourt ?? ''}
              onChange={e => setJudgeBasicData(prev => ({ ...prev, residingCourt: e.target.value }))}
              isIncorrect={validatedFields.includes('step5') && !judgeBasicData?.residingCourt}
              errorMessage={(validatedFields.includes('step5') && !judgeBasicData?.residingCourt) ? "This is a required field" : ""}
              errorCode='EC170'
            />
          </QuestionItemGeneralLayout>
          <QuestionItemGeneralLayout
            step={6}
            title='Number of years you have presided over this court'
            isValid={validateSteps['step6']}
          >
            <StandardInput
              label=""
              type="number"
              value={judgeBasicData?.yearsPresidingCourt ?? ''}
              onChange={({target: { value }}) => setJudgeBasicData(prev => ({ ...prev, yearsPresidingCourt: !value ? undefined : Number(value) }))}
              isIncorrect={validatedFields.includes('step6') && !judgeBasicData?.yearsPresidingCourt}
              errorMessage={(validatedFields.includes('step6') && !judgeBasicData?.yearsPresidingCourt) ? "This is a required field" : ""}
              errorCode='EC171'
            />
          </QuestionItemGeneralLayout>
          <QuestionItemGeneralLayout
            step={7}
            title='Number of years of overall judicial experience'
            isValid={validateSteps['step7']}
          >
            <StandardInput
              label=""
              type="number"
              value={judgeBasicData?.yearsOverallExperience ?? ''}
              onChange={({target: { value }}) => setJudgeBasicData(prev => ({ ...prev, yearsOverallExperience: !value ? undefined : Number(value) }))}
              isIncorrect={validatedFields.includes('step7') && !judgeBasicData?.yearsOverallExperience}
              errorMessage={(validatedFields.includes('step7') && !judgeBasicData?.yearsOverallExperience) ? "This is a required field" : ""}
              errorCode='EC172'
            />
          </QuestionItemGeneralLayout>
          <QuestionItemGeneralLayout
            step={8}
            title='Texas Bar information'
            leftBar
            isValid={validateSteps['step8']}
          >
            <BarCardNumberInput
              name="barCardNumber"
              onChange={e => setJudgeBasicData(prev => ({ ...prev, barCardNumber: e.target.value }))}
              value={judgeBasicData?.barCardNumber ?? ''}
              errorCode='EC173'
            />
            <StandardFormatInput
              label="Date admitted to Texas Bar"
              labelSize='high'
              maskStyle='date'
              leftIcon='calendar'
              value={judgeBasicData?.dateAdmittedTexasBar ?? ''}
              onValueChange={({ formattedValue }) => setJudgeBasicData(prev => ({ ...prev, dateAdmittedTexasBar: formattedValue }))}
              isIncorrect={validatedFields.includes('step8')}
              errorMessage={validatedFields.includes('step8') ? validateDateField(judgeBasicData.dateAdmittedTexasBar) : ''}
              errorCode='EC174'
            />
          </QuestionItemGeneralLayout>
        </>
      )}
    </FormContainer>
  )
}

export default BasicForm